import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { BasicFlex } from '../../../utils/Common';

import { toUpper } from 'lodash';

import { Grid, darken, lighten } from '@mui/material';

import Field from '../../../utils/FormikFields/Field';

import { useQuery } from '@tanstack/react-query';

import { useFormikContext } from 'formik';

import { useFormData } from '../../../contexts/FormData';

import { findDuplicateByID } from '../apiService';

import { getConsignedByVendor, getVendorIntegration } from "../../Shipments/apiService";

import ReportGmailerrorredRoundedIcon from '@mui/icons-material/ReportGmailerrorredRounded';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';

import SimpleChip from '../../../utils/utilities/SimpleChip';

import useDebounce from '../../../hooks/useDebounce';

import { formatCurrency } from '../../../utils/util';

const WarningMsg = styled(BasicFlex)`
  margin-top: .5rem;
  color: ${props => darken(props.theme.colors.error, .2)};
`

const ConsignedWig = styled(BasicFlex)`
  margin-top: 1.5rem;
  border: 1px dashed ${props => props.theme.colors.border};
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.$selected ? lighten(props.theme.colors.success, .93) : props.theme.colors.lightBg};
  color: ${props => props.$selected ? darken(props.theme.colors.success, .5) : "inherit"};
  cursor: pointer;
  /* max-width: 40rem; */

  &:hover {
    background-color: ${props => lighten(props.theme.colors.success, .8)};
  }
`

const Action = styled.div`
  width: 8rem;
`

const WigInfo = styled(BasicFlex)`
  
`;

const Code = styled.span`
  font-weight: 500;
`

const Sku = styled.div`
  font-family: 'Roboto Mono', 'SF Mono', Menlo, Consolas, monospace;
  font-size: 1.2rem;
  font-weight: bold;
  text-transform: uppercase;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 3px;
  /* letter-spacing: 0.2px; */
  /* width: max-content; */
`;

const PriceInfo = styled(BasicFlex)`
  gap: 1rem;
`;

const Amount = styled.div`
  font-weight: 500;
  font-size: 1.5rem;
`

const Pill = styled.div`
  border-radius: 10px;
  padding: .2rem 1rem;
  background-color: ${props => props.$color ? lighten(props.theme.colors.success, .8) : props.theme.colors.darkBg};
  color: ${props => props.$color ? darken(props.theme.colors.success, .8) : "inherit"};
  font-weight: 500;
  font-size: 1rem;
  width: max-content;
`

const MatchChip = styled(BasicFlex)`
  border-radius: 10px;
  padding: .4rem 1rem;
  background-color: ${props => lighten(props.theme.colors.success, .8)};
  color: ${props => darken(props.theme.colors.success, .5)};
  font-weight: 500;
  font-size: 1rem;
  width: max-content;
`

const ConnectionBoxStyles = styled(BasicFlex)`
  color: ${props => props.theme.colors.mildText};

  .brand_name {
    font-weight: 500;
    color: ${props => props.theme.colors.text};
  }
`

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$connected ?  lighten(props.theme.colors.success, 0) : props.theme.colors.mildText};
`

const EmptyDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1px solid ${props => props.theme.colors.mildText};
`

const RetailPill = ({ price, cost }) => {
  return ( price ?
    <Pill $color={true}>
      ${price} &middot; +{((price - cost) / cost * 100).toFixed(0)}%
    </Pill>
    : <Pill>No Retail Set</Pill>
  )
}

const ConnectionBox = ({ brand_name, isConnected, wig_id, noMatch }) => {
  let text, Icon;
  if (!brand_name || !isConnected) return null;
  if (!wig_id) {
    text = <span><span className='brand_name'>{brand_name}</span> is connected via NovoTracker, we'll find matching wigs as you type.</span>;
    Icon = <Dot $connected={isConnected} />
  } else {
    if (noMatch) {
      text = "No match in NovoTracker, enter details manually below.";
      Icon = <Dot />
    } else {
      text = <span><span className='brand_name'>{brand_name}</span> is connected via NovoTracker, we'll find matching wigs as you type.</span>;
      Icon = <Dot $connected={isConnected} />
    }
  }
  return ( <ConnectionBoxStyles width="auto" justify="flex-start" gap=".6rem" my=".6rem"> {Icon} <span>{text}</span> </ConnectionBoxStyles> )
}


const WigIdComponent = ({wigId}) => {

  const [foundWig, setFoundWig] = useState(null);
  const [selectedWig, setSelectedWig] = useState(null);

  const { values, setValues } = useFormikContext() || {};

  const { formValues, setFormValues } = useFormData() || {};

  const debouncedWigId = useDebounce(values?.wig_id, 250);

  const { data: wigs, isFetching: isFetchingWigs } = useQuery({
    queryKey: ['inventory', debouncedWigId, values?.brand_name],
    queryFn: () => findDuplicateByID(debouncedWigId, values?.brand_name),
    enabled: !!debouncedWigId && !!values?.brand_name
  })

  const { data: integration } = useQuery({
    queryKey: ["vendor-integration", formValues?.vendor_id],
    queryFn: () => getVendorIntegration(formValues?.vendor_id),
    enabled: !!formValues?.vendor_id,
  });

  // Connected only if the vendor has an actual integration record
  const connectedVendor = Array.isArray(integration)
    ? integration.length > 0
    : !!integration;

  const { data: consigned } = useQuery({
    queryKey: ["consigned", formValues?.vendor_id],
    queryFn: () => getConsignedByVendor(formValues?.vendor_id),
    enabled: !!formValues?.vendor_id && connectedVendor,
  });

  useEffect(() => {
    if (!consigned?.length || !debouncedWigId) {
      setFoundWig(null);
      return;
    }
    const found = consigned.find(el => toUpper(el.wig_id) === toUpper(debouncedWigId));
    const inStock = wigs?.find(el => toUpper(el.wig_id) === toUpper(debouncedWigId));
    setFoundWig(found && !inStock ? found : null);
  }, [wigs, consigned, debouncedWigId]);

  // MK9082

  const isTyping = values?.wig_id !== debouncedWigId;
  const consignedLoaded = !!consigned;
  const wig = wigs?.[0];

  const isChecking = !!debouncedWigId && (isTyping || isFetchingWigs || !consignedLoaded);
  const showNoMatch = 
    !!debouncedWigId &&
    !isTyping &&
    !isFetchingWigs &&
    consignedLoaded &&
    !foundWig &&
    !wig;

  const saveSelectedWig = (wig) => {
    setFormValues(ov => {
      return {
        ...ov,
        selectedWig: wig
      }
    })
  }

  useEffect(() => {
    if (formValues?.selectedWig) {
      handleSelect(formValues?.selectedWig)
    }
  }, [formValues?.selectedWig])

  const handleSelect = (wig) => {
    setValues(values => {
      return {
        ...values,
        color: wig?.color,
        length: wig?.length,
        length_unit: wig?.length_unit,
        cap_size: wig?.cap_size,
        material: wig?.material,
        hairstyle: wig?.hairstyle,
        coloring: wig?.coloring,
        layers: wig?.layers,
        price: wig?.price,
        cost_subtotal: wig?.cost,
      }
    })
  }

  return (
    <div style={{width: "100%"}}>
      <Field name="wig_id" label="Wig ID (leave empty for automatic ID)" type="text" />
      {wig && <WarningMsg justify="flex-start" gap=".8rem">
        <ReportGmailerrorredRoundedIcon />
        <div>Wig ID already exists for this vendor:</div> <SimpleChip label={<BasicFlex><div>{wig?.wig_id}</div> &nbsp; - &nbsp; <div>{wig?.sku}</div></BasicFlex>} palette="error" />
        </WarningMsg>}

        {!foundWig && <ConnectionBox brand_name={values?.brand_name} isConnected={connectedVendor} wig_id={values?.wig_id} noMatch={showNoMatch} />}

        {foundWig && !wig ? <ConsignedWig onClick={() => saveSelectedWig(foundWig)} $selected={!!formValues?.selectedWig}>
          <Action>
            {formValues?.selectedWig ? <DoneAllRoundedIcon fontSize="small" color="success" /> : <RadioButtonUnchecked fontSize="small" /> }
          </Action>

          <WigInfo dir="column">
            <BasicFlex width="fit-content" gap="1rem"><Code>{foundWig?.wig_id}</Code> <MatchChip gap=".5rem"><CheckRoundedIcon sx={{fontSize: "1.4rem"}} /> <span>Found Exact Match</span></MatchChip></BasicFlex>
            <Sku>{foundWig?.sku}</Sku>
          </WigInfo>

          <PriceInfo dir="column" align="flex-end">
            <Amount>{formatCurrency(foundWig?.cost)}</Amount>
            <RetailPill price={foundWig?.price} cost={foundWig?.cost} />
          </PriceInfo>
        </ConsignedWig> : null}
    </div>
  )
};

export default WigIdComponent;